import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Typography, Pagination, Stack } from "@mui/material";
import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import DateRangeFilter from "../components/common/DateRangeFilter";
import AppCircularProgress from "../components/common/AppCircularProgress";
import PaymentCard from "../components/payments/PaymentCard";
import useDebounce from "../hooks/useDebounce";
import { getPayments } from "../redux/actions/paymentActions";
import PageError from "../components/common/PageError";

const PaymentsPage = () => {
  const dispatch = useDispatch();
  const today = new Date().toISOString().split("T")[0];

  const { payments, pagination, loadingPayments, errorLoadingPayments } =
    useSelector((state) => state.payment);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [page, setPage] = useState(1);

  const fetchPaymentsList = useCallback(() => {
    dispatch(
      getPayments({
        page,
        size: 12,
        search: debouncedSearch,
        startDate,
        endDate,
      }),
    );
  }, [dispatch, page, debouncedSearch, startDate, endDate]);

  useEffect(() => {
    fetchPaymentsList();
  }, [fetchPaymentsList]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, startDate, endDate]);

  return (
    <PageContainer
      title="Payments History"
      subtitle="View all incoming and outgoing payments."
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search payments..."
      >
        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>

      {loadingPayments ? (
        <AppCircularProgress />
      ) : errorLoadingPayments ? (
        <PageError error={errorLoadingPayments} onClick={fetchPaymentsList} />
      ) : payments.length === 0 ? (
        <Typography mt={5} textAlign="center">
          No payments found for this period.
        </Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            {payments.map((payment) => (
              <Grid item key={payment.id} xs={12} sm={6} md={4} lg={3}>
                <PaymentCard payment={payment} />
              </Grid>
            ))}
          </Grid>

          {pagination && pagination.totalPages > 1 && (
            <Stack alignItems="flex-end" mt={3}>
              <Pagination
                count={pagination.totalPages}
                page={page}
                onChange={(e, val) => setPage(val)}
                color="primary"
              />
            </Stack>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default PaymentsPage;
